// client/src/components/servicos/FiltroServicos.js - VERSÃO COMPLETA COM SUPORTE A POOL
import React, { useState, useMemo } from 'react';
import { Search, X, Filter, Loader2, DollarSign, ChevronDown, ChevronUp } from 'lucide-react';
import PropTypes from 'prop-types';
import { useTenantI18n } from '../../hooks/useAccounts';
import { formatCurrency } from '../../utils/formatters';

const FiltroServicos = ({
    onFilterSubmit,
    loading,
    accountId,
    recursos = [],
    unidades = [],
    categorias = []
}) => {
    // ✅ ACHADO: displayValue de precoMinimo/precoMaximo era fixo em BRL,
    // sem ler configuracoesGerais.i18n da conta.
    const { currency, locale } = useTenantI18n();
    // Estado Inicial
    const initialFilters = {
        search: '',
        categoria: '',
        unidade: '',
        metadata_tipoServico: '',     // Internamente usamos underscore para ligar ao input
        agendavel: '',
        ativo: 'true',                // Padrão: Apenas ativos
        precoMinimo: '',
        precoMaximo: '',
        recursosVinculados: '',
        includeDeleted: false,
        visivelParaCliente: '',
        requerAnamnese: '',
        metadata_disponivelOnline: '',
        // ✅ NOVOS FILTROS PARA POOL
        usaPoolRecurso: '',
        temRecursosObrigatorios: '',
        poolRecursoId: ''
    };

    const [filtros, setFiltros] = useState(initialFilters);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showPriceFilter, setShowPriceFilter] = useState(false);

    // Memos para performance e segurança (evita erros se arrays forem null)
    const categoriasAtivas = useMemo(() => {
        return Array.isArray(categorias) ? categorias.filter(cat => cat.ativo !== false) : [];
    }, [categorias]);

    const unidadesAtivas = useMemo(() => {
        return Array.isArray(unidades) ? unidades.filter(u => u.ativo !== false) : [];
    }, [unidades]);

    const recursosAtivos = useMemo(() => {
        return Array.isArray(recursos) ? recursos.filter(r => r.ativo !== false) : [];
    }, [recursos]);

    // ✅ NOVO: Filtrar pools ativos
    const poolsAtivos = useMemo(() => {
        return Array.isArray(filtros.pools) ? filtros.pools.filter(p => p.ativo !== false) : [];
    }, [filtros.pools]);

    // Handler de Mudança nos Inputs
    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        let finalValue = type === 'checkbox' ? checked : value;
        
        setFiltros(prev => ({ 
            ...prev, 
            [name]: finalValue 
        }));
    };

    // ✅ FIX CRÍTICO: Processamento dos filtros antes de enviar
    const onSubmit = (e) => {
        e.preventDefault();
        
        const activeFilters = {};
        
        console.log('🔍 [FILTRO_SERVICOS] Processando filtros brutos:', filtros);
        
        Object.entries(filtros).forEach(([key, value]) => {
            // 1. Ignorar valores vazios
            if (value === '' || value === null || value === undefined) return;
            
            // 2. Normalizar Booleanos (ex: checkboxes)
            if (typeof value === 'boolean') {
                // Exceção: includeDeleted false não precisa ser enviado (é o padrão)
                if (key === 'includeDeleted' && value === false) return;
                activeFilters[key] = String(value);
                return;
            }

            // 3. Mapeamento e Achatamento (Flattening)
            switch(key) {
                // Envia 'tipoServico' em vez de 'metadata_tipoServico'
                case 'metadata_tipoServico':
                    if (value) activeFilters['tipoServico'] = value;
                    break;
                    
                // Envia 'disponivelOnline'
                case 'metadata_disponivelOnline':
                    if (value !== '') activeFilters['disponivelOnline'] = String(value === 'true' || value === true);
                    break;
                
                // Garante que booleanos em strings sejam explícitos
                case 'ativo':
                case 'agendavel':
                case 'visivelParaCliente':
                case 'requerAnamnese':
                case 'usaPoolRecurso':      // ✅ NOVO
                case 'temRecursosObrigatorios': // ✅ NOVO
                    activeFilters[key] = String(value === 'true' || value === true);
                    break;
                    
                default:
                    activeFilters[key] = value;
            }
        });
        
        console.log('📤 [FILTRO_SERVICOS] Enviando (Parametros Planos):', activeFilters);
        onFilterSubmit(activeFilters);
    };
    
    const onClear = () => {
        console.log('🧹 [FILTRO_SERVICOS] Limpando filtros');
        setFiltros(initialFilters);
        setShowAdvanced(false);
        setShowPriceFilter(false);
        // Envia objeto vazio para resetar a busca
        onFilterSubmit({});
    };

    // Verifica se há algum filtro ativo para mostrar o botão de limpar
    const hasActiveFilters = Object.entries(filtros).some(([key, value]) => {
        if (key === 'ativo' && value === 'true') return false; // Padrão
        if (value === '' || value === false || value === null || value === undefined) return false;
        if (key === 'includeDeleted' && value === false) return false;
        return true;
    });

    return (
        <form onSubmit={onSubmit} className="bg-white p-4 rounded-lg border border-gray-200 mb-6 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 items-end">
                
                {/* 1. Busca Textual */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="search" className="text-xs font-semibold text-gray-600 uppercase">
                        Busca
                    </label>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                        <input 
                            type="text" 
                            name="search" 
                            id="search" 
                            placeholder="Nome, descrição, tags..." 
                            value={filtros.search} 
                            onChange={onChange} 
                            className="form-input pl-10 text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all w-full"
                        />
                    </div>
                </div>

                {/* 2. Categoria */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="categoria" className="text-xs font-semibold text-gray-600 uppercase">
                        Categoria
                    </label>
                    <select 
                        name="categoria" 
                        id="categoria" 
                        value={filtros.categoria} 
                        onChange={onChange} 
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                    >
                        <option value="">Todas as categorias</option>
                        {categoriasAtivas.map(cat => (
                            <option key={cat._id} value={cat._id}>{cat.nome}</option>
                        ))}
                    </select>
                </div>

                {/* 3. Unidade */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="unidade" className="text-xs font-semibold text-gray-600 uppercase">
                        Unidade
                    </label>
                    <select 
                        name="unidade" 
                        id="unidade" 
                        value={filtros.unidade} 
                        onChange={onChange} 
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                    >
                        <option value="">Todas as unidades</option>
                        {unidadesAtivas.map(u => (
                            <option key={u._id} value={u._id}>
                                {u.nome} {u.localidade ? `- ${u.localidade}` : ''}
                            </option>
                        ))}
                    </select>
                </div>

                {/* 4. Status */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="ativo" className="text-xs font-semibold text-gray-600 uppercase">
                        Status
                    </label>
                    <select 
                        name="ativo" 
                        id="ativo" 
                        value={filtros.ativo} 
                        onChange={onChange} 
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                    >
                        <option value="">Todos os status</option>
                        <option value="true">Ativos</option>
                        <option value="false">Inativos</option>
                    </select>
                </div>

                {/* 5. Tipo de Serviço */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="metadata_tipoServico" className="text-xs font-semibold text-gray-600 uppercase">
                        Tipo
                    </label>
                    <select 
                        name="metadata_tipoServico" 
                        id="metadata_tipoServico" 
                        value={filtros.metadata_tipoServico} 
                        onChange={onChange} 
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                    >
                        <option value="">Todos os tipos</option>
                        <option value="interno">Interno</option>
                        <option value="externo">Externo</option>
                    </select>
                </div>

                {/* 6. Agendável */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="agendavel" className="text-xs font-semibold text-gray-600 uppercase">
                        Agendável
                    </label>
                    <select 
                        name="agendavel" 
                        id="agendavel" 
                        value={filtros.agendavel} 
                        onChange={onChange} 
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                    >
                        <option value="">Todos</option>
                        <option value="true">Sim</option>
                        <option value="false">Não</option>
                    </select>
                </div>

                {/* 7. Recurso Vinculado */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="recursosVinculados" className="text-xs font-semibold text-gray-600 uppercase">
                        Recurso
                    </label>
                    <select 
                        name="recursosVinculados" 
                        id="recursosVinculados" 
                        value={filtros.recursosVinculados} 
                        onChange={onChange} 
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                    >
                        <option value="">Todos os recursos</option>
                        {recursosAtivos.map(r => (
                            <option key={r._id} value={r._id}>
                                {r.nome} {r.tipo ? `(${r.tipo})` : ''}
                            </option>
                        ))}
                    </select>
                </div>

                {/* 8. Botão de Filtro de Preço */}
                <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-600 uppercase">
                        Preço
                    </label>
                    <button
                        type="button"
                        onClick={() => setShowPriceFilter(!showPriceFilter)}
                        className={`form-input text-sm py-2 px-3 rounded border transition-all bg-white flex items-center justify-between ${
                            showPriceFilter || filtros.precoMinimo || filtros.precoMaximo
                                ? 'border-blue-500 text-blue-600'
                                : 'border-gray-300 text-gray-700'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <DollarSign size={14} />
                            <span>Faixa de preço</span>
                        </div>
                        {showPriceFilter ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                </div>

                {/* Filtros de Preço (Expandidos) */}
                {showPriceFilter && (
                    <>
                        <div className="flex flex-col gap-1">
                            <label htmlFor="precoMinimo" className="text-xs font-semibold text-gray-600 uppercase">
                                Preço Mínimo
                            </label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">R$</span>
                                <input 
                                    type="number" 
                                    name="precoMinimo" 
                                    id="precoMinimo" 
                                    placeholder="0,00" 
                                    min="0"
                                    step="0.01"
                                    value={filtros.precoMinimo} 
                                    onChange={onChange} 
                                    className="form-input pl-10 text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                                />
                            </div>
                        </div>

                        <div className="flex flex-col gap-1">
                            <label htmlFor="precoMaximo" className="text-xs font-semibold text-gray-600 uppercase">
                                Preço Máximo
                            </label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">R$</span>
                                <input 
                                    type="number" 
                                    name="precoMaximo" 
                                    id="precoMaximo" 
                                    placeholder="9999,99" 
                                    min="0"
                                    step="0.01"
                                    value={filtros.precoMaximo} 
                                    onChange={onChange} 
                                    className="form-input pl-10 text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                                />
                            </div>
                        </div>
                    </>
                )}

                {/* Botões de Ação */}
                <div className="flex gap-2 h-full items-end md:col-span-2 lg:col-span-1 xl:col-span-1">
                    <button 
                        type="button" 
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        className={`bg-gray-100 hover:bg-gray-200 border rounded py-2 px-3 flex items-center justify-center transition-colors h-[38px] w-10 shrink-0 ${
                            showAdvanced 
                                ? 'border-blue-500 text-blue-600' 
                                : 'border-gray-300 text-gray-700'
                        }`}
                        title={showAdvanced ? "Menos filtros" : "Mais filtros"}
                    >
                        <Filter size={16} />
                    </button>

                    {/* Antes reinventava cor do zero em Tailwind (bg-blue-600) e o
                        texto "Filtrar" so aparecia acima de 640px (hidden sm:inline)
                        — mesmo padrao ja corrigido nos outros filtros do app */}
                    <button
                        type="submit"
                        disabled={loading}
                        className="action-button action-button-primary flex-1 justify-center"
                    >
                        {loading ? <Loader2 size={18} className="icon animate-spin" /> : <Search size={18} className="icon" />}
                        Aplicar Filtros
                    </button>

                    {hasActiveFilters && (
                        <button
                            type="button"
                            onClick={onClear}
                            disabled={loading}
                            className="action-button action-button-secondary justify-center"
                            title="Limpar Filtros"
                        >
                            <X size={18} className="icon" />
                        </button>
                    )}
                </div>
            </div>

            {/* Filtros Avançados */}
            {showAdvanced && (
                <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="flex flex-col gap-1">
                        <label htmlFor="visivelParaCliente" className="text-xs font-semibold text-gray-600 uppercase">
                            Visível Cliente
                        </label>
                        <select 
                            name="visivelParaCliente" 
                            id="visivelParaCliente" 
                            value={filtros.visivelParaCliente} 
                            onChange={onChange} 
                            className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                        >
                            <option value="">Todos</option>
                            <option value="true">Sim</option>
                            <option value="false">Não</option>
                        </select>
                    </div>

                    <div className="flex flex-col gap-1">
                        <label htmlFor="requerAnamnese" className="text-xs font-semibold text-gray-600 uppercase">
                            Requer Anamnese
                        </label>
                        <select 
                            name="requerAnamnese" 
                            id="requerAnamnese" 
                            value={filtros.requerAnamnese} 
                            onChange={onChange} 
                            className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                        >
                            <option value="">Todos</option>
                            <option value="true">Sim</option>
                            <option value="false">Não</option>
                        </select>
                    </div>

                    {/* Disponível Online */}
                    <div className="flex flex-col gap-1">
                        <label htmlFor="metadata_disponivelOnline" className="text-xs font-semibold text-gray-600 uppercase">
                            Disponível Online
                        </label>
                        <select 
                            name="metadata_disponivelOnline" 
                            id="metadata_disponivelOnline" 
                            value={filtros.metadata_disponivelOnline} 
                            onChange={onChange} 
                            className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                        >
                            <option value="">Todos</option>
                            <option value="true">Sim</option>
                            <option value="false">Não</option>
                        </select>
                    </div>

                    <div className="flex flex-col gap-1">
                        <label htmlFor="includeDeleted" className="text-xs font-semibold text-gray-600 uppercase">
                            Incluir Excluídos
                        </label>
                        <select 
                            name="includeDeleted" 
                            id="includeDeleted" 
                            value={filtros.includeDeleted} 
                            onChange={onChange} 
                            className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                        >
                            <option value={false}>Não</option>
                            <option value={true}>Sim</option>
                        </select>
                    </div>

                    {/* ✅ NOVO: Filtrar por Pool de Recursos */}
                    <div className="flex flex-col gap-1">
                        <label htmlFor="usaPoolRecurso" className="text-xs font-semibold text-gray-600 uppercase">
                            Usa Pool de Recursos
                        </label>
                        <select 
                            name="usaPoolRecurso" 
                            id="usaPoolRecurso" 
                            value={filtros.usaPoolRecurso} 
                            onChange={onChange} 
                            className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                        >
                            <option value="">Todos</option>
                            <option value="true">Sim</option>
                            <option value="false">Não</option>
                        </select>
                    </div>

                    {/* ✅ NOVO: Filtrar por Recursos Obrigatórios */}
                    <div className="flex flex-col gap-1">
                        <label htmlFor="temRecursosObrigatorios" className="text-xs font-semibold text-gray-600 uppercase">
                            Tem Recursos Obrigatórios
                        </label>
                        <select 
                            name="temRecursosObrigatorios" 
                            id="temRecursosObrigatorios" 
                            value={filtros.temRecursosObrigatorios} 
                            onChange={onChange} 
                            className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                        >
                            <option value="">Todos</option>
                            <option value="true">Sim</option>
                            <option value="false">Não</option>
                        </select>
                    </div>

                    {/* ✅ NOVO: Filtrar por Pool Específico */}
                    <div className="flex flex-col gap-1">
                        <label htmlFor="poolRecursoId" className="text-xs font-semibold text-gray-600 uppercase">
                            Pool Específico
                        </label>
                        <select 
                            name="poolRecursoId" 
                            id="poolRecursoId" 
                            value={filtros.poolRecursoId} 
                            onChange={onChange} 
                            className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                        >
                            <option value="">Todos os pools</option>
                            {poolsAtivos.map(p => (
                                <option key={p._id} value={p._id}>
                                    {p.nome} ({p.tipo}) - {p.quantidade} recursos
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            {/* Resumo de Filtros Ativos (Tags) */}
            {hasActiveFilters && (
                <div className="mt-4 pt-3 border-t border-gray-200 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-blue-800 whitespace-nowrap">Filtros ativos:</span>
                        {Object.entries(filtros).map(([key, value]) => {
                            if (value === '' || value === false || value === null || value === undefined) return null;
                            if (key === 'ativo' && value === 'true') return null; // Não mostra filtro padrão
                            if (key === 'includeDeleted' && value === false) return null;
                            
                            let displayValue = value;
                            let displayKey = key;
                            
                            // Tradução para exibição
                            const keyTranslations = {
                                'search': 'Busca',
                                'categoria': 'Categoria',
                                'unidade': 'Unidade',
                                'metadata_tipoServico': 'Tipo',
                                'agendavel': 'Agendável',
                                'ativo': 'Status',
                                'recursosVinculados': 'Recurso',
                                'precoMinimo': 'Preço Mín.',
                                'precoMaximo': 'Preço Máx.',
                                'visivelParaCliente': 'Visível Cliente',
                                'requerAnamnese': 'Requer Anamnese',
                                'metadata_disponivelOnline': 'Online',
                                'includeDeleted': 'Incluir Excluídos',
                                'usaPoolRecurso': 'Usa Pool',
                                'temRecursosObrigatorios': 'Recursos Fixos',
                                'poolRecursoId': 'Pool'
                            };
                            
                            displayKey = keyTranslations[key] || key;
                            
                            // Formatação de valores
                            if (value === 'true' || value === true) displayValue = 'Sim';
                            else if (value === 'false' || value === false) displayValue = 'Não';
                            else if (key === 'categoria') {
                                displayValue = categorias.find(c => c._id === value)?.nome || '...';
                            } else if (key === 'unidade') {
                                displayValue = unidades.find(u => u._id === value)?.nome || '...';
                            } else if (key === 'recursosVinculados') {
                                displayValue = recursos.find(r => r._id === value)?.nome || '...';
                            } else if (key === 'poolRecursoId') {
                                displayValue = poolsAtivos.find(p => p._id === value)?.nome || '...';
                            } else if (['precoMinimo', 'precoMaximo'].includes(key)) {
                                displayValue = formatCurrency(value, currency, locale);
                            } else if (key === 'usaPoolRecurso') {
                                displayValue = value === 'true' ? 'Sim' : 'Não';
                            } else if (key === 'temRecursosObrigatorios') {
                                displayValue = value === 'true' ? 'Sim' : 'Não';
                            }
                            
                            return (
                                <span 
                                    key={key} 
                                    className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium whitespace-nowrap text-xs flex items-center gap-1"
                                >
                                    {displayKey}: {displayValue}
                                </span>
                            );
                        })}
                    </div>
                    <button 
                        type="button" 
                        onClick={onClear} 
                        className="text-red-600 hover:text-red-800 hover:underline whitespace-nowrap text-xs font-medium"
                    >
                        Limpar todos
                    </button>
                </div>
            )}
        </form>
    );
};

FiltroServicos.propTypes = {
    onFilterSubmit: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    accountId: PropTypes.string,
    recursos: PropTypes.array,
    unidades: PropTypes.array,
    categorias: PropTypes.array
};

FiltroServicos.defaultProps = {
    loading: false,
    recursos: [],
    unidades: [],
    categorias: []
};

export default FiltroServicos;